/**
 * cart-drawer-custom - Extends modal-element with cart drawer functionality
 * Uses normal DOM like modal-element but with advanced drawer features
 */

import ModalElement from 'modal';
import 'generic-slider';

'use strict';

class CartDrawerCustom extends ModalElement {
    constructor() {
        super();
        this.sectionId = this.getAttribute('section-id') || null;
        this.abortController = null;
    }

    connectedCallback() {
        // Call the base connectedCallback from modal-element
        super.connectedCallback();

        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');

        // Initialize abort controller for event listeners
        this.abortController = new AbortController();

        // Save the section ID (may be replaced in the DOM)
        if (!this.sectionId) {
            this.sectionId = this.extractSectionId();
        }

        // Set up cart-specific event listeners
        this.setupCartListeners();
    }

    disconnectedCallback() {
        // Clean up event listeners
        if (this.abortController) {
            this.abortController.abort();
        }
    }

    setupCartListeners() {
        const signal = this.abortController.signal;

        // Listen to cart events
        document.addEventListener('cart:prepare-bundled-sections', (e) => {
            this.onBundleSection(e);
        }, { signal });

        document.addEventListener('cart:change', (e) => {
            this.onCartChange(e);
        }, { signal });

        document.addEventListener('cart:refresh', () => {
            this.refreshCart();
        }, { signal });

        window.addEventListener('pageshow', (e) => {
            this.onPageShow(e);
        }, { signal });
    }

    /**
     * Extract section ID from the closest shopify-section
     */
    extractSectionId() {
        const section = this.closest('.shopify-section');
        if (section && section.id) {
            return section.id.replace('shopify-section-', '');
        }
        return document.querySelector('.shopify-section--cart-drawer')?.id.replace('shopify-section-', '') || null;
    }

    /**
     * Get initial focus element (no focus for drawer)
     */
    get initialFocus() {
        return false;
    }

    /**
     * Override open to handle drawer-specific behavior
     */
    async open(trigger = null, triggerType = null) {
        // Lock body scroll (drawer behavior)
        document.documentElement.classList.add('lock');

        // Add drawer-specific class
        this.classList.add('is-open');

        // Call the open method from modal-element
        super.open(trigger, triggerType);

        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('cart-drawer:opened', {
            bubbles: true,
            detail: { drawer: this }
        }));
    }

    /**
     * Override close to handle drawer-specific behavior
     */
    close() {
        // Unlock body scroll
        document.documentElement.classList.remove('lock');

        // Remove drawer-specific class
        this.classList.remove('is-open');

        // Call the close method from modal-element
        super.close();

        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('cart-drawer:closed', {
            bubbles: true,
            detail: { drawer: this }
        }));
    }

    /**
     * This method is called when the cart is changing, and allows custom sections to order a "re-render"
     */
    onBundleSection(event) {
        event.detail.sections.add(this.sectionId);
    }

    /**
     * When the cart changes, we need to re-render the cart drawer
     */
    async onCartChange(event) {
        if (!event.detail || !event.detail.cart) return;

        const cart = event.detail.cart;
        const sections = cart.sections || {};

        // Get the section HTML
        let sectionHtml = sections[this.sectionId];
        if (!sectionHtml) {
            const response = await fetch(`${Shopify.routes.root}?section_id=${this.sectionId}`);
            sectionHtml = await response.text();
        }

        // Replace full content
        await this.replaceContent(sectionHtml);

        // Auto-open drawer when item is added
        if (window.themeVariables?.settings?.cartType === 'drawer' &&
            !this.isOpen &&
            event.detail.baseEvent === 'variant:add') {
            this.open();
        }
    }

    /**
     * Handle page show event (e.g., back button navigation)
     */
    async onPageShow(event) {
        if (event.persisted) {
            await this.refreshCart();
        }
    }

    /**
     * Refresh cart content from server
     */
    async refreshCart() {
        try {
            const response = await fetch(`${Shopify.routes.root}?section_id=${this.sectionId}`);
            const html = await response.text();
            await this.replaceContent(html);
        } catch (error) {
            console.error('Error refreshing cart:', error);
        }
    }

    /**
     * Replace drawer content with new HTML
     */
    async replaceContent(html) {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newCartDrawerElement = doc.getElementById(`shopify-section-${this.sectionId}`)?.querySelector('cart-drawer-custom');

            if (newCartDrawerElement) {
                const fragment = document.createRange().createContextualFragment(newCartDrawerElement.innerHTML);

                // Get cart count
                const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
                const cartData = await cartResponse.json();
                const itemCount = cartData.item_count || 0;

                // Replace children
                this.replaceChildren(...fragment.children);

                // Dispatch refreshed event
                this.dispatchEvent(new CustomEvent('cart-drawer:refreshed', {
                    bubbles: true,
                    detail: { itemCount }
                }));

                this.init();
            }
        } catch (error) {
            console.error('Error replacing cart content:', error);
        }
    }
}

// Register the custom element
if (!customElements.get('cart-drawer-custom')) {
    customElements.define('cart-drawer-custom', CartDrawerCustom);
}
